import React, { useState } from 'react'
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Autocomplete,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { ISO_COUNTRY_CODES } from '@/constants/countries'
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator'

export const UserProfile: React.FC = () => {
  const { user, updateProfile, changePassword } = useAuth()
  const { t } = useTranslation()

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    city: user?.city || '',
    country: user?.country || '',
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [error, setError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [success, setSuccess] = useState(false)
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  const countryOptions = ISO_COUNTRY_CODES.map((code) => ({
    code,
    label: t(`projects.countries.${code}`),
  })).sort((a, b) => a.label.localeCompare(b.label))

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    setError('')
    setSuccess(false)
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    })
    setPasswordError('')
    setPasswordSuccess(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess(false)

    // Validate required fields
    if (!formData.firstName || !formData.lastName) {
      setError(t('user.profilePage.requiredFields'))
      setLoading(false)
      return
    }

    try {
      await updateProfile(formData)
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('user.profilePage.updateError'))
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordLoading(true)
    setPasswordError('')
    setPasswordSuccess(false)

    // Validate required fields
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError(t('user.profilePage.password.requiredFields'))
      setPasswordLoading(false)
      return
    }

    // Validate password strength
    const requirements = [
      passwordData.newPassword.length >= 8,
      /[A-Z]/.test(passwordData.newPassword),
      /[a-z]/.test(passwordData.newPassword),
      /\d/.test(passwordData.newPassword),
    ]
    const metRequirements = requirements.filter(Boolean).length

    if (metRequirements < 3) {
      setPasswordError(t('user.profilePage.password.weakPassword'))
      setPasswordLoading(false)
      return
    }

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError(t('auth.register.passwordMismatch'))
      setPasswordLoading(false)
      return
    }

    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword)
      setPasswordSuccess(true)
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : t('user.profilePage.password.updateError'))
    } finally {
      setPasswordLoading(false)
    }
  }

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ padding: 4 }}>
          <Typography component="h1" variant="h4" gutterBottom>
            {t('user.profilePage.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('user.profilePage.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('user.profilePage.updateSuccess')}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              {/* Read-only fields */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="username"
                  label={t('auth.fields.username')}
                  value={user?.username || ''}
                  disabled
                  helperText={t('user.profilePage.usernameReadonly')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="email"
                  label={t('auth.fields.email')}
                  value={user?.email || ''}
                  disabled
                  helperText={t('user.profilePage.emailReadonly')}
                />
              </Grid>

              {/* Editable fields */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="firstName"
                  label={t('auth.fields.firstName')}
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label={t('auth.fields.lastName')}
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="city"
                  label={t('auth.fields.city')}
                  name="city"
                  autoComplete="address-level2"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="country"
                  options={countryOptions}
                  getOptionLabel={(option) => option.label}
                  value={countryOptions.find((c) => c.code === formData.country) || null}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      country: newValue?.code || '',
                    })
                    setError('')
                    setSuccess(false)
                  }}
                  disabled={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('auth.fields.country')}
                      autoComplete="country-name"
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('user.profilePage.updateButton')}
            </Button>
          </Box>
        </Paper>

        {/* Change Password Section - Only for internal users */}
        {user?.source === 'INTERNAL' && (
          <Paper elevation={3} sx={{ padding: 4, mt: 3 }}>
            <Typography component="h2" variant="h5" gutterBottom>
              {t('user.profilePage.password.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('user.profilePage.password.subtitle')}
            </Typography>

            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}

            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('user.profilePage.password.updateSuccess')}
              </Alert>
            )}

            <Box component="form" onSubmit={handlePasswordSubmit} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="currentPassword"
                    label={t('user.profilePage.password.currentPassword')}
                    type={showCurrentPassword ? 'text' : 'password'}
                    id="currentPassword"
                    autoComplete="current-password"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    disabled={passwordLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            edge="end"
                          >
                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="newPassword"
                    label={t('user.profilePage.password.newPassword')}
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword"
                    autoComplete="new-password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    disabled={passwordLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {passwordData.newPassword && (
                  <Grid item xs={12}>
                    <PasswordStrengthIndicator password={passwordData.newPassword} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="confirmPassword"
                    label={t('auth.fields.confirmPassword')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    autoComplete="new-password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    disabled={passwordLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={passwordLoading}
              >
                {passwordLoading ? <CircularProgress size={24} /> : t('user.profilePage.password.updateButton')}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  )
}
